import React, { useEffect, useRef, useState } from 'react';
import { Grid, Button } from '@mui/material';
import { blogCategoryNavStyle as classes } from './blogCategoryNavStyle';
import { Text } from '@web-for-marketing/react-ui';
import { RightArrowIcon } from '@components/IconComponents/RightArrowIcon';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { useAtom } from 'jotai';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { debounce } from '@helpers/debounceHelper';

export interface BlogCategoryData {
    text: string;
    link: string;
}

export interface BlogCategoryNavProp {
    categoryLinks: BlogCategoryData[];
}

export function BlogCategoryNav({ categoryLinks }: BlogCategoryNavProp): React.ReactElement {
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const blogHomeURL = getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.BLOG_HOME);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            updateScrollArrows();
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.unobserve(container);
        };
    }, []);

    const updateScrollArrows = (): void => {
        const container = containerRef.current;
        if (!container) return;

        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(container.scrollLeft + container.clientWidth < container.scrollWidth);
    };
    const handleScroll = (direction: 'left' | 'right'): void => {
        const container = containerRef.current;
        const adjustment = direction === 'left' ? -100 : 100;
        if (container) {
            container.scrollTo({
                left: container.scrollLeft + adjustment,
                behavior: 'smooth',
            });
            updateScrollArrows();
        }
    };

    const handleScrollDebounced = debounce(updateScrollArrows, 100);
    return (
        <div css={classes.navContainer}>
            {showLeftArrow ? (
                <div css={classes.arrowContainer}>
                    <Button
                        onClick={() => handleScroll('left')}
                        css={classes.arrowButton}
                        aria-hidden
                        data-testid='left-arrow'
                        tabIndex={-1}
                    >
                        <RightArrowIcon css={[classes.flipArrow, classes.arrowIcon]} />
                    </Button>
                </div>
            ) : null}
            <Grid
                container
                css={classes.categoryContainer}
                ref={containerRef}
                data-testid='category-container'
                onScroll={handleScrollDebounced}
            >
                <div css={classes.navItem}>
                    <Text variant='body2' css={classes.navLink} weight='bold' component='a' href={blogHomeURL}>
                        Geotab Blog
                    </Text>
                </div>
                <div css={classes.separator}></div>
                {categoryLinks.map((section, index) => (
                    <div key={index} css={classes.navItem}>
                        <Text variant='body2' css={classes.navLink} component='a' href={section.link}>
                            {section.text}
                        </Text>
                    </div>
                ))}
            </Grid>
            {showRightArrow ? (
                <div css={classes.arrowContainer}>
                    <Button
                        onClick={() => handleScroll('right')}
                        css={classes.arrowButton}
                        data-testid='right-arrow'
                        aria-hidden
                        tabIndex={-1}
                    >
                        <RightArrowIcon css={classes.arrowIcon} />
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
